import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, Sparkles, Loader2, AlertTriangle, Route } from "lucide-react";
import AddressForm from "@/components/AddressForm";
import { useAuth } from "@/lib/auth";

const SIZE_TIERS = [
  { key: "envelope", title: "Envelope",  weight: "up to 0.5 kg",  example: "Documents and papers, flat" },
  { key: "small",    title: "Small",     weight: "up to 2 kg",    example: "Fits in a backpack" },
  { key: "medium",   title: "Medium",    weight: "2 – 5 kg",       example: "Cabin bag sized" },
  { key: "large",    title: "Large",     weight: "5 – 15 kg",      example: "Large suitcase sized" },
  { key: "xl",       title: "Extra Large",        weight: "15 – 30 kg",     example: "Needs a car boot" },
];

// Decode Google's encoded polyline algorithm 5 → [[lat, lng], ...]
const decodePolyline = (str) => {
  if (!str) return [];
  let index = 0, lat = 0, lng = 0;
  const coords = [];
  while (index < str.length) {
    let result = 0, shift = 0, b;
    do { b = str.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lat += (result & 1) ? ~(result >> 1) : (result >> 1);
    result = 0; shift = 0;
    do { b = str.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lng += (result & 1) ? ~(result >> 1) : (result >> 1);
    coords.push([lat / 1e5, lng / 1e5]);
  }
  return coords;
};

const PostParcel = ({ onCreated }) => {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({
    size: "small", description: "", declared_value: 1500,
    category: "general", photo_id: null,
    prohibited_agreed: false, insurance: false,
    pickup_point: null, drop_point: null,
    distance_km: 0, duration_min: 0,
  });
  const [distanceState, setDistanceState] = useState({ loading: false, error: "" });
  const [polyline, setPolyline] = useState(null);
  const [polylineFallback, setPolylineFallback] = useState(false);
  const [quote, setQuote] = useState(null); // { sender_total } — no breakdown for sender
  const [busy, setBusy] = useState(false);
  const [showProhib, setShowProhib] = useState(false);

  const mapEl = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => { api.get("/config").then(r => setConfig(r.data)).catch(() => {}); }, []);

  // Distance + directions auto-trigger the moment BOTH addresses have been selected via autocomplete.
  useEffect(() => {
    if (!form.pickup_point || !form.drop_point) {
      setPolyline(null); setQuote(null); setDistanceState({ loading: false, error: "" });
      setForm(f => ({ ...f, distance_km: 0, duration_min: 0 }));
      return;
    }
    const p = form.pickup_point, d = form.drop_point;
    setDistanceState({ loading: true, error: "" });
    api.get(`/geo/route?from_lat=${p.lat}&from_lng=${p.lng}&to_lat=${d.lat}&to_lng=${d.lng}`)
      .then((r) => {
        if (!r.data.distance_km || r.data.distance_km <= 0) {
          setDistanceState({ loading: false, error: "No route between these two addresses. Please pick different points." });
          return;
        }
        setForm(f => ({ ...f, distance_km: r.data.distance_km, duration_min: r.data.duration_min }));
        setDistanceState({ loading: false, error: "" });
      })
      .catch(() => setDistanceState({ loading: false, error: "Couldn't calculate driving distance. Please try again." }));

    // Directions polyline (best-effort — straight-line fallback if disabled)
    api.get(`/geo/directions?from_lat=${p.lat}&from_lng=${p.lng}&to_lat=${d.lat}&to_lng=${d.lng}`)
      .then((r) => { setPolyline(decodePolyline(r.data.polyline)); setPolylineFallback(false); })
      .catch(() => { setPolyline([[p.lat, p.lng], [d.lat, d.lng]]); setPolylineFallback(true); });
    // eslint-disable-next-line
  }, [form.pickup_point?.lat, form.pickup_point?.lng, form.drop_point?.lat, form.drop_point?.lng]);

  // Live INDICATIVE range whenever distance/size changes — final price is per-carrier on the next screen
  useEffect(() => {
    if (form.distance_km > 0) {
      // Ask backend for range using a temp payload; simplest is min/max via /pricing/quote calls
      Promise.all([
        api.post("/pricing/quote", { travel_mode: "bike", distance_km: form.distance_km, size: form.size, declared_value: form.declared_value, insurance: form.insurance || form.declared_value >= 20000 }),
        api.post("/pricing/quote", { travel_mode: "suv",  distance_km: form.distance_km, size: form.size, declared_value: form.declared_value, insurance: form.insurance || form.declared_value >= 20000 }),
      ]).then(([a, b]) => setQuote({ price_low: a.data.sender_total, price_high: b.data.sender_total }))
        .catch(() => setQuote(null));
    } else { setQuote(null); }
  }, [form.distance_km, form.size, form.declared_value, form.insurance]);

  // Render route on preview map
  useEffect(() => {
    if (!mapEl.current || !form.pickup_point || !form.drop_point) return;
    if (!mapRef.current) {
      mapRef.current = L.map(mapEl.current, { zoomControl: false, dragging: false, scrollWheelZoom: false, doubleClickZoom: false });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: '&copy; OpenStreetMap' }).addTo(mapRef.current);
    }
    const m = mapRef.current;
    m.eachLayer(l => { if (l instanceof L.Marker || l instanceof L.Polyline) m.removeLayer(l); });
    const p = form.pickup_point, d = form.drop_point;
    L.marker([p.lat, p.lng]).addTo(m).bindTooltip("Pickup");
    L.marker([d.lat, d.lng]).addTo(m).bindTooltip("Drop");
    if (polyline?.length >= 2) {
      L.polyline(polyline, { color: polylineFallback ? "#94a3b8" : "#D38138", weight: 4, dashArray: polylineFallback ? "6 6" : null }).addTo(m);
    }
    m.fitBounds([[p.lat, p.lng], [d.lat, d.lng]], { padding: [24, 24] });
  }, [polyline, polylineFallback, form.pickup_point, form.drop_point]);

  const handleUpload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, photo_id: r.data.file_id }));
      toast.success("Photo uploaded");
    } catch (e) { toast.error("Upload failed"); }
  };

  const submit = async () => {
    if (!form.pickup_point || !form.drop_point) return toast.error("Search and select both pickup and drop addresses");
    if (distanceState.loading) return toast.error("Please wait for the distance to compute");
    if (!form.distance_km || form.distance_km <= 0) return toast.error("We couldn't calculate the driving distance. Pick different addresses or try again.");
    if (!form.prohibited_agreed) return toast.error("Confirm prohibited items policy");
    if (!form.pickup_point?.details || !form.drop_point?.details) return toast.error("Fill house/flat details for both addresses");
    setBusy(true);
    try {
      const payload = {
        ...form,
        pickup_location: `${form.pickup_point.area || form.pickup_point.city}${form.pickup_point.city ? ", " + form.pickup_point.city : ""}`,
        drop_location: `${form.drop_point.area || form.drop_point.city}${form.drop_point.city ? ", " + form.drop_point.city : ""}`,
        pickup_details: form.pickup_point.details || "",
        drop_details: form.drop_point.details || "",
        recipient_phone: form.recipient_phone || "",
      };
      const r = await api.post("/parcels", payload);
      toast.success("Parcel created · pick a traveller next");
      onCreated?.(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
      {user?.size_mismatch_flags_count > 0 && (
        <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-3 flex items-start gap-3" data-testid="size-flag-banner">
          <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5"/>
          <div className="text-xs text-amber-900 leading-snug">
            <div className="font-semibold">A carrier has previously flagged your parcel as bigger than declared ({user.size_mismatch_flags_count} time{user.size_mismatch_flags_count > 1 ? "s" : ""}).</div>
            Please pick the size that reflects the <b>actual</b> weight and volume — repeated mismatches may pause your account.
          </div>
        </div>
      )}
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-4 h-4 text-amber-600"/>
        <div className="text-xs text-slate-500 uppercase tracking-wider">Post a parcel</div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <AddressForm label="Pickup" value={form.pickup_point} onChange={(pt) => setForm(f => ({ ...f, pickup_point: pt }))} allowCurrentLocation allowSavedAddresses/>
        <AddressForm label="Drop"   value={form.drop_point}   onChange={(pt) => setForm(f => ({ ...f, drop_point: pt }))} allowSavedAddresses/>
      </div>

      {/* Route preview + distance + price — appears the moment both points are selected */}
      <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider">
            <Route className="w-3.5 h-3.5 text-amber-600"/> Route preview
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500">Estimated range</div>
            <div className="font-display text-xl font-extrabold text-slate-900" data-testid="live-sender-total">
              {quote?.price_low != null ? `₹${Math.round(quote.price_low)}–₹${Math.round(quote.price_high)}` : "—"}
            </div>
            <div className="text-[10px] text-slate-400">Final price shown per carrier</div>
          </div>
        </div>

        <div className="mt-3">
          {distanceState.loading && (
            <div className="text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/> Calculating driving distance…</div>
          )}
          {distanceState.error && (
            <div className="text-sm text-rose-700 inline-flex items-center gap-2"><AlertTriangle className="w-4 h-4"/> {distanceState.error}</div>
          )}
          {!distanceState.loading && !distanceState.error && form.distance_km > 0 && (
            <div className="text-sm font-mono text-slate-700" data-testid="parcel-distance-display">
              {form.distance_km} km · ~{Math.round(form.duration_min || 0)} min drive
            </div>
          )}
          {!distanceState.loading && !distanceState.error && !form.distance_km && (
            <div className="text-sm text-slate-400">Select both addresses to see driving distance and price.</div>
          )}
          {polylineFallback && !distanceState.error && (
            <div className="text-[11px] text-slate-400 mt-1">Route line is approximate — enable the Directions API to see the exact road path.</div>
          )}
        </div>

        {form.pickup_point && form.drop_point && (
          <div ref={mapEl} className="mt-3 h-56 rounded-xl overflow-hidden border border-slate-200" data-testid="route-preview-map"/>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <div className="sm:col-span-2">
          <label className="text-xs text-slate-500 uppercase tracking-wider">Parcel size</label>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="size-grid">
            {SIZE_TIERS.map((s) => (
              <label
                key={s.key}
                data-testid={`size-opt-${s.key}`}
                className={`cursor-pointer rounded-xl border-2 px-3 py-2.5 transition ${form.size === s.key ? "border-amber-500 bg-amber-50/60" : "border-slate-200 hover:border-slate-300"}`}
              >
                <input type="radio" name="size" value={s.key} checked={form.size === s.key} onChange={() => setForm(f => ({...f, size: s.key}))} className="sr-only"/>
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-semibold text-slate-900">{s.title}</div>
                  <div className="text-[11px] font-mono text-slate-500">{s.weight}</div>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">{s.example}</div>
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs text-slate-500 uppercase tracking-wider">Category</label>
          <Select value={form.category} onValueChange={(v) => setForm(f => ({...f, category: v}))}>
            <SelectTrigger data-testid="category-select" className="rounded-xl mt-1"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="general">General</SelectItem>
              <SelectItem value="perishable">Perishable</SelectItem>
              <SelectItem value="liquid">Liquid</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-slate-500 uppercase tracking-wider">Declared value (₹)</label>
          <Input data-testid="value-input" type="number" value={form.declared_value} onChange={e => setForm(f => ({...f, declared_value: parseFloat(e.target.value || 0)}))} className="rounded-xl mt-1"/>
          <div className="text-xs text-slate-500 mt-1">Your tier cap will be enforced.</div>
        </div>
        <div>
          <label className="text-xs text-slate-500 uppercase tracking-wider">Photo</label>
          <div className="mt-1 flex items-center gap-3">
            <label className="cursor-pointer inline-flex items-center gap-2 rounded-xl border border-dashed border-slate-300 px-4 py-2.5 text-sm text-slate-500 hover:bg-slate-50">
              <Upload className="w-4 h-4"/> Upload
              <input data-testid="photo-input" type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])}/>
            </label>
            {form.photo_id && <Badge className="bg-emerald-100 text-emerald-700">Uploaded</Badge>}
          </div>
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs text-slate-500 uppercase tracking-wider">Description</label>
          <Textarea data-testid="desc-input" value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} className="rounded-xl mt-1" placeholder="What's inside?"/>
        </div>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <Checkbox data-testid="prohib-checkbox" checked={form.prohibited_agreed} onCheckedChange={(v) => setForm(f => ({...f, prohibited_agreed: !!v}))}/>
        <div className="text-sm text-slate-600">
          I confirm this parcel does not contain <button type="button" className="underline" onClick={() => setShowProhib(true)}>prohibited items</button>.
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4">
        <div className="flex items-start gap-3">
          <Checkbox data-testid="insurance-checkbox" checked={form.insurance || form.declared_value >= 20000} disabled={form.declared_value >= 20000} onCheckedChange={(v) => setForm(f => ({...f, insurance: !!v}))}/>
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-900">Add insurance cover · 2% of declared value</div>
            <div className="text-xs text-amber-800/80 mt-0.5">
              {form.declared_value >= 20000
                ? "Mandatory for parcels ≥ ₹20,000 · included automatically."
                : "Optional peace-of-mind cover for loss or damage."}
            </div>
          </div>
        </div>
      </div>

      <Button
        data-testid="submit-parcel-btn"
        disabled={busy || distanceState.loading || !form.distance_km}
        onClick={submit}
        className="mt-6 w-full rounded-full bg-slate-900 hover:bg-slate-800"
      >
        {busy ? "Creating…" : "Create parcel · browse travellers"}
      </Button>

      <Dialog open={showProhib} onOpenChange={setShowProhib}>
        <DialogContent>
          <DialogHeader><DialogTitle>Prohibited items</DialogTitle></DialogHeader>
          <ul className="text-sm text-slate-600 list-disc pl-5 space-y-1">
            <li>Cash, jewellery above your tier cap</li>
            <li>Weapons or ammunition</li>
            <li>Illegal substances or contraband</li>
            <li>Hazardous materials (flammables, corrosives)</li>
            <li>Live animals</li>
          </ul>
          <DialogFooter><Button onClick={() => setShowProhib(false)}>Got it</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PostParcel;
